// Actor context -- the DERIVED source of every event.
//
// `source` (human vs agent) is never a string literal at a call site. It comes
// from the current actor: the GUI runs as 'human' (the default), and the
// in-browser agent API scopes its calls with runAsActor('agent', ...) so the
// SAME event-emitting code paths emit events tagged source:'agent'.
//
// Two surfaces to read the actor:
//   - React:        useActor() / <ActorProvider>
//   - imperative:   getActor() / runAsActor() -- for non-React call sites (track)
//                   and the agent API, which is a plain window global.
//
// Scoping model (architecture review 2026-09-23, 0.3): runAsActor scopes ONLY
// the synchronous part of the call it wraps. The previous implementation held
// the scope open across `await fn()` by saving and restoring ONE module-level
// value, so two overlapping agent calls could restore each other's saved value
// out of order (A saves human, B saves agent, A ends -> human while B runs,
// B ends -> restores agent) and every later human event was tagged agent until
// reload; human events fired during an agent call's await were mis-tagged too.
// Synchronous scopes cannot interleave (JS runs one stack at a time), so a
// push/pop scope stack is exact, and no awaited gap can leak.
//
// Code that emits AFTER an await on behalf of the call that started it
// captures the actor at its synchronous entry with captureActor() and emits
// through the returned runner (the throttles and the markup wrappers in
// ViewerMarkupContext do this). This is the "carry the actor with the work"
// rule in small; the dispatch envelope carrying it is the longer-term form.
//
// `baseActor` is the ambient actor outside any scope (setActor /
// ActorProvider). ActorProvider mirrors it into React state for components that
// want to display or react to it.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type Actor = 'human' | 'agent';
export type Surface = 'gui' | 'agent-api';

let baseActor: Actor = 'human';
/** Active synchronous runAsActor scopes, innermost last. */
const scopes: Actor[] = [];

const surfaceOf = (actor: Actor): Surface => (actor === 'human' ? 'gui' : 'agent-api');

/** Read the current actor (imperative; read by track()). */
export function getActor(): Actor {
    return scopes.length > 0 ? scopes[scopes.length - 1] : baseActor;
}

/** Read the current surface (imperative; read by track()). */
export function getSurface(): Surface {
    return surfaceOf(getActor());
}

/** Set the ambient actor imperatively (the surface follows it). */
export function setActor(actor: Actor): void {
    baseActor = actor;
}

/** Number of active synchronous scopes (tests and diagnostics only). */
export function activeActorScopes(): number {
    return scopes.length;
}

function runScopedSync<T>(actor: Actor, fn: () => T): T {
    scopes.push(actor);
    try {
        return fn();
    } finally {
        scopes.pop();
    }
}

/**
 * Run `fn` with `actor` (and its surface) as the current actor for the
 * SYNCHRONOUS part of the call only, then await its result outside the scope.
 * Anything `fn` emits before its first internal `await` is tagged `actor`;
 * work after an await is NOT (another caller -- e.g. the human GUI -- may run
 * in that gap). Code that emits after an await on the caller's behalf uses
 * captureActor() at its synchronous entry.
 */
export async function runAsActor<T>(actor: Actor, fn: () => T | Promise<T>): Promise<T> {
    return await runScopedSync(actor, fn);
}

/**
 * Capture the current actor NOW and return a runner that re-scopes it for a
 * later synchronous emission (after an await, a timer, or a debounce).
 */
export function captureActor(): <T>(fn: () => T) => T {
    const actor = getActor();
    return (fn) => runScopedSync(actor, fn);
}

interface ActorContextValue {
    actor: Actor;
    setActor: (actor: Actor) => void;
}

const ActorContext = createContext<ActorContextValue>({ actor: 'human', setActor: () => {} });

export function ActorProvider({ children }: { children: ReactNode }) {
    const [actor, setActorState] = useState<Actor>(baseActor);

    const set = useCallback((next: Actor) => {
        setActor(next);
        setActorState(next);
    }, []);

    const value = useMemo<ActorContextValue>(() => ({ actor, setActor: set }), [actor, set]);

    return <ActorContext.Provider value={value}>{children}</ActorContext.Provider>;
}

/** React hook to read/set the current actor. */
export function useActor(): ActorContextValue {
    return useContext(ActorContext);
}
